import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * @typedef {Object} ExtendedWarrantyRefs
 * @property {HTMLInputElement} hiddenCostInput
 * @property {HTMLInputElement} hiddenTypeInput
 * @property {HTMLInputElement} hiddenTotalInput
 * @property {HTMLInputElement} hiddenTotalCentsInput
 * @property {HTMLInputElement} hiddenProductIdInput
 */

/**
 * @extends {Component<ExtendedWarrantyRefs>}
 */
class ExtendedWarranty extends Component {
  basePriceCents = 0;
  unitWarrantyCents = 0;
  selectedWarrantyCents = 0;
  currentQuantity = 1;

  connectedCallback() {
    super.connectedCallback();

    this.basePriceCents = Number(this.dataset.basePriceCents || 0);
    this.currentQuantity = this.getQuantity();

    const radios = /** @type {NodeListOf<HTMLInputElement>} */ (
      this.querySelectorAll('input[type="radio"][name="properties[Garantia Extendida]"]')
    );

    if (!radios.length) return;

    radios.forEach((radio) => {
      radio.addEventListener('change', this.onChange);
    });

    const section = this.closest('.shopify-section, dialog');
    section?.addEventListener(ThemeEvents.variantUpdate, this.onVariantUpdate);
    section?.addEventListener(ThemeEvents.quantitySelectorUpdate, this.onQuantityUpdate);

    void this.syncFromSelection();
    this.updateTotal();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    const radios = /** @type {NodeListOf<HTMLInputElement>} */ (
      this.querySelectorAll('input[type="radio"][name="properties[Garantia Extendida]"]')
    );

    radios.forEach((radio) => {
      radio.removeEventListener('change', this.onChange);
    });

    const section = this.closest('.shopify-section, dialog');
    section?.removeEventListener(ThemeEvents.variantUpdate, this.onVariantUpdate);
    section?.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.onQuantityUpdate);
  }

  /**
   * Gets the current product quantity from the quantity selector in the same form.
   * @returns {number}
   */
  getQuantity() {
    const form = this.closest('form');
    if (!form) return 1;
    const quantityInput = form.querySelector('quantity-selector-component input[ref="quantityInput"]');
    if (!(quantityInput instanceof HTMLInputElement)) return 1;
    const qty = parseInt(quantityInput.value, 10);
    return Number.isNaN(qty) || qty < 1 ? 1 : qty;
  }

  onChange = () => {
    void this.syncFromSelection();
  };

  /** @param {Event} event */
  onQuantityUpdate = (event) => {
    const detail = /** @type {any} */ (event)?.detail;
    const quantity = Number(detail?.quantity || 0);
    if (quantity > 0) {
      this.currentQuantity = quantity;
      void this.syncFromSelection();
    }
  };

  /** @param {Event} event */
  onVariantUpdate = (event) => {
    const detail = /** @type {any} */ (event)?.detail;
    const price = Number(detail?.resource?.price || 0);
    if (!Number.isNaN(price) && price > 0) {
      this.basePriceCents = price;
      this.updateTotal();
    }
  };

  async syncFromSelection() {
    const hiddenCostInput = /** @type {HTMLInputElement | undefined} */ (this.refs.hiddenCostInput);
    const hiddenTypeInput = /** @type {HTMLInputElement | undefined} */ (this.refs.hiddenTypeInput);
    const hiddenTotalInput = /** @type {HTMLInputElement | undefined} */ (this.refs.hiddenTotalInput);

    if (!hiddenCostInput) return;

    const selected = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[type="radio"][name="properties[Garantia Extendida]"]:checked')
    );

    if (!selected) {
      hiddenCostInput.value = '';
      if (hiddenTypeInput) hiddenTypeInput.value = '';
      if (hiddenTotalInput) hiddenTotalInput.value = '';
      this.selectedWarrantyCents = 0;
      this.updateTotal();
      return;
    }

    const duration = selected.dataset.warrantyDuration || selected.value;
    const cost = selected.dataset.warrantyCost || '';
    const productId = this.dataset.productId;
    const type = selected.dataset.warrantyType || '';
    const imageUrl = selected.dataset.warrantyImageUrl || '';

    const costMajor = Number(cost || 0);
    const unitWarrantyCents = Number.isNaN(costMajor) ? 0 : Math.round(costMajor * 100);
    this.unitWarrantyCents = unitWarrantyCents;
    this.selectedWarrantyCents = unitWarrantyCents * this.currentQuantity;

    hiddenCostInput.value = cost;
    if (hiddenTypeInput) {
      hiddenTypeInput.value = type;
    }

    // Exponer la URL de la imagen en el dataset del componente para que product-form.js la use
    this.dataset.selectedWarrantyImageUrl = imageUrl;

    this.updateTotal();
    // if (hiddenTotalInput) {
    //   hiddenTotalInput.value = ((this.basePriceCents + this.selectedWarrantyCents) / 100).toFixed(2);
    // }

    console.log('selected warranty', {
      duration,
      cost,
      type,
      productId,
      imageUrl,
      dataset: selected.dataset,
    });
  }

  updateTotal() {
    const hiddenTotalInput = /** @type {HTMLInputElement | undefined} */ (this.refs.hiddenTotalInput);
    const hiddenTotalCentsInput = /** @type {HTMLInputElement | undefined} */ (this.refs.hiddenTotalCentsInput);

    // Costo unitario de la garantía → para el carrito (cart-summary.liquid multiplica por quantity)
    const unitCents = this.unitWarrantyCents || 0;
    const unitMajor = (unitCents / 100).toFixed(2);

    // Total PDP = precio base + garantía × cantidad → para mostrar visualmente
    const pdpTotalCents = this.basePriceCents + this.selectedWarrantyCents;
    const pdpTotalMajor = (pdpTotalCents / 100).toFixed(2);

    if (hiddenTotalInput) {
      hiddenTotalInput.value = unitMajor;
    }
    if (hiddenTotalCentsInput) {
      hiddenTotalCentsInput.value = String(unitCents);
    }

    // Exponer en el DOM para que product-form.js lo lea
    this.dataset.totalCents = String(pdpTotalCents);
    this.dataset.warrantyUnitCents = String(unitCents);

    // Try multiple strategies to find the product-price node (different themes mark it differently)
    let productPrice = this.closest('.shopify-section, dialog')?.querySelector(
      `product-price[data-product-id="${this.dataset.productId}"]`
    );
    if (!productPrice) {
      // Fallback: try searching within the closest form or globally
      productPrice = this.closest('form')?.querySelector('product-price') || document.querySelector(
        `product-price[data-product-id="${this.dataset.productId}"]`
      ) || document.querySelector('product-price');
    }
    if (!productPrice) {
      // Nothing to update visually; keep hidden inputs updated
      console.warn('extended-warranty: product-price node not found for product', this.dataset.productId);
      return;
    }

    // Prefer visible price nodes but fall back to any .price found
    const visiblePriceSelector =
      '.price__regular:not(.price__hidden) .price, .price__sale:not(.price__hidden) .price-item--sale.price, .price, .price-item--sale, .price-item--regular';
    const visiblePrice = productPrice.querySelector(visiblePriceSelector);
    if (!(visiblePrice instanceof HTMLElement)) {
      console.warn('extended-warranty: visible price element not found inside product-price for', this.dataset.productId);
      return;
    }

    const locale = document.documentElement.lang || 'es-DO';
    const currency = /** @type {any} */ (window.Shopify)?.currency?.active || 'DOP';
    const formattedTotal = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
    }).format(pdpTotalCents / 100);

    visiblePrice.textContent = formattedTotal;
    visiblePrice.setAttribute('data-warranty-adjusted', 'true');
    visiblePrice.setAttribute('data-warranty-cents', String(this.selectedWarrantyCents));
    visiblePrice.setAttribute('data-price-with-warranty', pdpTotalMajor);
    visiblePrice.setAttribute('data-base-price', (this.basePriceCents / 100).toFixed(2));

    const allPriceNodes = productPrice.querySelectorAll('.price');
    allPriceNodes.forEach((node) => {
      if (node !== visiblePrice) {
        node.removeAttribute('data-warranty-adjusted');
        node.removeAttribute('data-warranty-cents');
        node.removeAttribute('data-price-with-warranty');
        node.removeAttribute('data-base-price');
      }
    });

    const comparePrice = productPrice.querySelector('.price-item--regular.compare-at-price');
    if (comparePrice instanceof HTMLElement) {
      comparePrice.removeAttribute('data-warranty-adjusted');
    }
  }
}

if (!customElements.get('extended-warranty-component')) {
  customElements.define('extended-warranty-component', ExtendedWarranty);
}